import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealerAPI, type Dealer, type DealerPhone, type OemStats } from '@/lib/supabase/dealers';
import {
  setDealerConfig,
  DealerConfigError,
  SET_DEALER_CONFIG_MAX_IDS,
  type DealerConfigPatch,
  type DealerConfigRow
} from '@/lib/supabase/dealerConfig';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { formatAddressCase } from '@/lib/utils/addressCase';
import { logUserActivity } from '@/lib/activity/userActivity';
import { generateHumanDescription } from '@/lib/activity/humanDescriptions';
import { patchDealerRows, applyConfigRows, countAlsoAffected } from './dealerCacheUpdates';

export interface SimpleDealerData {
  dealer_id: string;
  seller_name: string;
  seller_address: string;
  dealer_city: string;
  dealer_region: string;
  /** Main number (or the single stored one for internal tenants) */
  dealer_phone: string;
  /** Every number, main first; [] for internal tenants, which use dealer_phone */
  phone_numbers: DealerPhone[];
  dealer_inventory_url: string;
  dealer_type: string;
  seller_latitude?: number | undefined;
  seller_longitude?: number | undefined;
  /** Open recalls (campaign × vehicle), as on the recall map; undefined until the backend has it */
  recall_count: number | undefined;
  /** Vehicles with at least one open recall */
  vehicle_count: number;
  is_included: boolean;
  notes: string;
  alias_names: string[];
  oem_stats: OemStats;
  is_internal: boolean;
}

export interface DealerWithPreferences extends SimpleDealerData {
  status: 'included' | 'excluded';
}

interface DealersCache {
  dealers: SimpleDealerData[];
}

// Transform Dealer to SimpleDealerData format
function transformDealerToSimpleData(dealer: Dealer): SimpleDealerData {
  return {
    dealer_id: dealer.dealer_id,
    seller_name: dealer.dealer_name,
    // Feeds mix "403 W DIVISION ST" and "Market ST. Ne"; one case style for display and filters
    seller_address: formatAddressCase(dealer.seller_address || ''),
    dealer_city: formatAddressCase(dealer.seller_city || ''),
    dealer_region: dealer.seller_region || '',
    dealer_phone: dealer.phone || '',
    phone_numbers: dealer.phone_numbers ?? [],
    dealer_inventory_url: dealer.detail_url || '',
    dealer_type: dealer.dealer_type || '',
    seller_latitude: dealer.seller_latitude,
    seller_longitude: dealer.seller_longitude,
    recall_count: dealer.recalls,
    vehicle_count: dealer.vehicles,
    is_included: dealer.is_included ?? true,
    notes: dealer.notes || '',
    alias_names: dealer.alias_names,
    oem_stats: dealer.oem_stats,
    is_internal: dealer.is_internal
  };
}

/**
 * Query function for dealers data (exported for prefetching)
 */
export async function fetchDealersData(
  userId: string,
  tenantId: string | null
): Promise<DealersCache> {
  if (!userId || !tenantId) {
    return { dealers: [] };
  }
  // One row per dealer; include/exclude and notes are tenant-level and on the row.
  const dealers = await dealerAPI.getDealersWithPreferences(userId);
  return { dealers: dealers.map(transformDealerToSimpleData) };
}

export const dealersKey = (userId: string | undefined, tenantId: string | null) =>
  ['dealers', userId, tenantId] as const;

/**
 * React Query hook to fetch dealers with their (tenant-level) settings
 * Uses aggressive caching (15 min staleTime) since dealer data changes infrequently
 */
export function useDealers(userId?: string) {
  const tenantId = useCurrentTenantId();

  return useQuery({
    queryKey: dealersKey(userId, tenantId),
    queryFn: () => fetchDealersData(userId ?? '', tenantId),
    enabled: !!userId && !!tenantId,
    staleTime: 15 * 60 * 1000, // 15 minutes - dealers change infrequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Use cached data if available
    retry: 2
  });
}

/**
 * Dealers with a display status. Memoized so `dealers` keeps its identity
 * until the cache changes.
 */
export function useDealersWithPreferences(userId?: string) {
  const { data, isLoading, error } = useDealers(userId);

  const dealers = useMemo(
    () =>
      (data?.dealers ?? []).map(
        (dealer): DealerWithPreferences => ({
          ...dealer,
          status: dealer.is_included ? 'included' : 'excluded'
        })
      ),
    [data]
  );

  const errorMessage = error instanceof Error ? error.message : 'Failed to load dealers';
  return {
    dealers,
    loading: isLoading,
    error: error ? errorMessage : null
  };
}

function logDealerActivity(
  action: 'toggle_dealer_inclusion' | 'update_dealer_notes' | 'sync_dealers',
  metadata: Record<string, unknown>,
  extra: { entityName?: string; itemCount?: number } = {}
) {
  logUserActivity({
    category: 'dealer_config',
    action,
    entityType: 'dealer',
    ...extra,
    metadata: {
      ...metadata,
      human_description: generateHumanDescription('dealer_config', action, metadata)
    }
  });
}

interface ConfigContext {
  previousData?: DealersCache | undefined;
  /** First targeted dealer before the change (audit metadata) */
  previous?: SimpleDealerData | undefined;
}

/**
 * set_dealer_config with an optimistic update, then the server's returned state.
 * - A stale list (PT404: a dealer was merged) or a multi-chunk bulk failure
 *   refetches instead of rolling back, since earlier writes may have landed.
 * - A write that also reached same-named dealers (`also_affects`) refetches.
 */
function useDealerConfigMutation<V>(
  userId: string | undefined,
  toConfig: (variables: V) => { dealerIds: string[]; patch: DealerConfigPatch },
  onLogged: (variables: V, context: ConfigContext | undefined) => void
) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();
  const key = dealersKey(userId, tenantId);
  const refetch = () => queryClient.invalidateQueries({ queryKey: key });

  return useMutation<DealerConfigRow[], Error, V, ConfigContext>({
    mutationFn: variables => {
      const { dealerIds, patch } = toConfig(variables);
      return setDealerConfig(dealerIds, patch);
    },
    onMutate: async variables => {
      const { dealerIds, patch } = toConfig(variables);
      await queryClient.cancelQueries({ queryKey: key });
      const previousData = queryClient.getQueryData<DealersCache>(key);
      if (previousData) {
        queryClient.setQueryData<DealersCache>(key, {
          dealers: patchDealerRows(previousData.dealers, dealerIds, patch)
        });
      }
      const previous = previousData?.dealers.find(d => d.dealer_id === dealerIds[0]);
      return { previousData, previous };
    },
    onError: (error, variables, context) => {
      const partialWrite = toConfig(variables).dealerIds.length > SET_DEALER_CONFIG_MAX_IDS;
      if (context?.previousData) queryClient.setQueryData(key, context.previousData);
      if (partialWrite || (error instanceof DealerConfigError && error.kind === 'stale')) {
        refetch();
      }
    },
    onSuccess: (rows, variables, context) => {
      queryClient.setQueryData<DealersCache>(key, old =>
        old ? { dealers: applyConfigRows(old.dealers, rows) } : old
      );
      if (countAlsoAffected(rows) > 0) refetch();
      onLogged(variables, context);
    }
  });
}

/**
 * Mutation hook for including/excluding a single dealer
 */
export function useUpdateDealerPreference(userId?: string) {
  return useDealerConfigMutation(
    userId,
    (v: { dealerId: string; dealerName: string; isIncluded: boolean }) => ({
      dealerIds: [v.dealerId],
      patch: { isIncluded: v.isIncluded }
    }),
    (v, context) => {
      const metadata = {
        dealer_name: v.dealerName,
        is_included: v.isIncluded,
        was_included: context?.previous?.is_included
      };
      logDealerActivity('toggle_dealer_inclusion', metadata, { entityName: v.dealerName });
    }
  );
}

/**
 * Mutation hook for bulk including/excluding dealers (chunked server calls)
 */
export function useBulkUpdateDealerPreferences(userId?: string) {
  return useDealerConfigMutation(
    userId,
    (v: { dealerIds: string[]; isIncluded: boolean }) => ({
      dealerIds: v.dealerIds,
      patch: { isIncluded: v.isIncluded }
    }),
    v => {
      const metadata = { bulk: true, count: v.dealerIds.length, is_included: v.isIncluded };
      logDealerActivity('toggle_dealer_inclusion', metadata, { itemCount: v.dealerIds.length });
    }
  );
}

/**
 * Mutation hook for updating dealer notes
 */
export function useUpdateDealerNotes(userId?: string) {
  return useDealerConfigMutation(
    userId,
    (v: { dealerId: string; dealerName: string; notes: string }) => ({
      dealerIds: [v.dealerId],
      patch: { notes: v.notes }
    }),
    (v, context) => {
      const oldNotes = context?.previous?.notes;
      const metadata = { dealer_name: v.dealerName, had_notes: Boolean(oldNotes?.trim()) };
      logDealerActivity('update_dealer_notes', metadata, { entityName: v.dealerName });
    }
  );
}

/**
 * Mutation hook for syncing dealers from materialized view
 * This syncs unique dealers from prod.cars_with_safety_recalls_mv to prod.dealers
 */
export function useSyncDealers(userId?: string) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: () => dealerAPI.syncDealers(),
    onSuccess: () => {
      logDealerActivity('sync_dealers', {});
      // Invalidate dealers query to refetch with new data, plus any related queries
      queryClient.invalidateQueries({ queryKey: dealersKey(userId, tenantId) });
      queryClient.invalidateQueries({ queryKey: ['dealers'] });
    }
  });
}
